/**
 * Record mode's engine (SEO_BRIEF_08 section 2.1): the microphone, MediaRecorder, the timer, the
 * level, the 90-minute cap and the screen wake lock, as a state machine with no DOM of its own.
 * lib/mic-panel.ts draws it; web/test/recorder.test.ts drives it with a stubbed getUserMedia,
 * MediaRecorder and clock.
 *
 *   idle ─start→ asking ─(microphone allowed)→ recording ⇄ paused
 *                  │                              │ stop, or the cap, or the microphone ending
 *                  └─(refused)→ idle + a failure  └→ stopping ─(the recorder's last data)→ stopped
 *
 * `stopped` holds the recording: a Blob in the type the browser recorded (WebM/Opus in Chrome and
 * Edge, WebM or Ogg/Opus in Firefox, MP4/AAC in Safari), its extension and its length. Starting
 * again from `stopped` drops it. NOTHING HERE SENDS ANYTHING: the recording leaves the browser only
 * when the panel hands it to B6 on Transcribe.
 */

/** The most one recording may run, the audio pages' 90 minutes (server/db/config.py). */
export const RECORD_CAP_S = 90 * 60;
/** From here the panel shows the time left. */
export const WARN_FROM_S = 80 * 60;
/** How often the timer and the level are read while recording. */
export const TICK_MS = 100;
/**
 * Speech at 64 kbit/s is clear in Opus and AAC alike, and 90 minutes of it is about 43 MB, under
 * the upload cap with room to spare. A browser's own default (128 kbit/s in some) would put a
 * long recording near the cap.
 */
export const AUDIO_BITS_PER_SECOND = 64_000;

/** In order of preference; the first the browser takes is used, or its own default when none. */
export const RECORDER_TYPES: readonly string[] = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/aac',
];

export type RecorderState = 'idle' | 'asking' | 'recording' | 'paused' | 'stopping' | 'stopped';
export type RecorderFailure = 'blocked' | 'no-mic' | 'busy' | 'cannot-record' | 'failed' | 'empty';
export type RecordingExtension = 'webm' | 'ogg' | 'm4a';

export interface Recording {
  readonly blob: Blob;
  /** The recorder's own type, parameters and all, e.g. `audio/webm;codecs=opus`. */
  readonly type: string;
  readonly extension: RecordingExtension;
  /** Seconds recorded, pauses left out. */
  readonly seconds: number;
}

/* ── the browser, as far as this module needs it ───────────────────────────── */

export interface RecorderLike {
  readonly mimeType: string;
  readonly state: 'inactive' | 'recording' | 'paused';
  start(timesliceMs?: number): void;
  stop(): void;
  pause?(): void;
  resume?(): void;
  ondataavailable: ((event: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: ((event: unknown) => void) | null;
}

export interface RecorderCtor {
  new (
    stream: MediaStream,
    options?: { mimeType?: string; audioBitsPerSecond?: number },
  ): RecorderLike;
  isTypeSupported?(type: string): boolean;
}

export interface WakeLockLike {
  release(): Promise<void>;
  addEventListener?(type: 'release', listener: () => void): void;
}

/** Reads the microphone's loudness, 0 to 1. */
export interface LevelReader {
  read(): number;
  close(): void;
}

export interface RecorderEnv {
  readonly getUserMedia?:
    | ((constraints: MediaStreamConstraints) => Promise<MediaStream>)
    | undefined;
  readonly MediaRecorder?: RecorderCtor | undefined;
  readonly now: () => number;
  readonly setInterval: (fn: () => void, ms: number) => unknown;
  readonly clearInterval: (handle: unknown) => void;
  /** `navigator.wakeLock.request('screen')`, where the browser has it. */
  readonly requestWakeLock?: (() => Promise<WakeLockLike>) | undefined;
  /** Calls `fn` whenever the page becomes visible again; returns the unsubscribe. */
  readonly onVisible?: ((fn: () => void) => () => void) | undefined;
  /** An analyser on the stream, or null where there is no Web Audio. */
  readonly levelReader?: ((stream: MediaStream) => LevelReader | null) | undefined;
}

export interface RecorderEvents {
  onState?(state: RecorderState): void;
  /** Seconds recorded so far, on every tick while recording. */
  onTick?(seconds: number): void;
  onLevel?(level: number): void;
  onDone?(recording: Recording): void;
  onFail?(failure: RecorderFailure): void;
  /** The recording reached RECORD_CAP_S and was stopped. */
  onCapped?(): void;
  /** Whether the screen is being kept awake (false: the browser can't). */
  onWakeLock?(held: boolean): void;
}

export interface Recorder {
  readonly state: RecorderState;
  readonly canPause: boolean;
  /** The recorder's type once recording, or the one it will ask for. */
  readonly type: string;
  seconds(): number;
  start(): Promise<void>;
  pause(): void;
  resume(): void;
  stop(): void;
  /** Stop without keeping anything: leaving the page, or a reset. */
  discard(): void;
}

/* ── pure helpers ───────────────────────────────────────────────────────────── */

/** The first of RECORDER_TYPES the browser records in, or '' for its own default. */
export function pickType(isTypeSupported: ((type: string) => boolean) | undefined): string {
  if (typeof isTypeSupported !== 'function') return '';
  for (const type of RECORDER_TYPES) {
    try {
      if (isTypeSupported(type)) return type;
    } catch {
      /* a browser that throws here takes its default */
    }
  }
  return '';
}

/** The file extension for a recorder type. Safari's MP4 audio is saved as .m4a, its own name. */
export function extensionFor(type: string): RecordingExtension {
  const t = type.toLowerCase();
  if (t.includes('ogg')) return 'ogg';
  if (t.includes('mp4') || t.includes('aac') || t.includes('m4a')) return 'm4a';
  return 'webm';
}

/** `m:ss`, or `h:mm:ss` from an hour. */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/** What a refused getUserMedia means for the visitor, by the DOMException's name. */
export function failureOf(err: unknown): RecorderFailure {
  const name = typeof err === 'object' && err !== null ? (err as { name?: unknown }).name : null;
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
    case 'PermissionDeniedError':
      return 'blocked';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'no-mic';
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'busy';
    default:
      return 'failed';
  }
}

/** A name for the download and the upload: the transcript's title is this name. */
export function recordingName(extension: RecordingExtension, at: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const day = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  return `recording-${day}-${pad(at.getHours())}${pad(at.getMinutes())}.${extension}`;
}

/* ── the machine ───────────────────────────────────────────────────────────── */

export function createRecorder(env: RecorderEnv, events: RecorderEvents = {}): Recorder {
  let state: RecorderState = 'idle';
  let stream: MediaStream | null = null;
  let recorder: RecorderLike | null = null;
  let chunks: Blob[] = [];
  let chosenType = env.MediaRecorder
    ? pickType(env.MediaRecorder.isTypeSupported?.bind(env.MediaRecorder))
    : '';
  /** Seconds banked by the segments before the current one. */
  let banked = 0;
  /** When the current segment started, or null while paused or idle. */
  let segmentStart: number | null = null;
  let ticker: unknown = null;
  let level: LevelReader | null = null;
  let wake: WakeLockLike | null = null;
  let unwatch: (() => void) | null = null;
  let capped = false;
  /** Bumped by every start and discard, so a late microphone answer cannot revive an old run. */
  let generation = 0;

  function setState(next: RecorderState): void {
    state = next;
    events.onState?.(next);
  }

  function seconds(): number {
    const running = segmentStart === null ? 0 : (env.now() - segmentStart) / 1000;
    return banked + running;
  }

  function bank(): void {
    if (segmentStart !== null) {
      banked += (env.now() - segmentStart) / 1000;
      segmentStart = null;
    }
  }

  function tick(): void {
    if (state !== 'recording') return;
    const s = seconds();
    events.onTick?.(Math.min(s, RECORD_CAP_S));
    if (level) events.onLevel?.(level.read());
    if (s >= RECORD_CAP_S) {
      capped = true;
      stop();
    }
  }

  async function holdScreen(): Promise<void> {
    if (!env.requestWakeLock) {
      events.onWakeLock?.(false);
      return;
    }
    try {
      const sentinel = await env.requestWakeLock();
      if (state !== 'recording' && state !== 'paused') {
        void sentinel.release().catch(() => undefined);
        return;
      }
      wake = sentinel;
      events.onWakeLock?.(true);
    } catch {
      // Refused (a hidden tab, a battery saver): the panel shows the line.
      events.onWakeLock?.(false);
    }
  }

  /** The browser drops a wake lock when the page is hidden; take it again on return. */
  function watchVisibility(): void {
    if (!env.onVisible || unwatch) return;
    unwatch = env.onVisible(() => {
      if (state === 'recording' || state === 'paused') void holdScreen();
    });
  }

  function letGo(): void {
    if (ticker !== null) {
      env.clearInterval(ticker);
      ticker = null;
    }
    level?.close();
    level = null;
    events.onLevel?.(0);
    if (stream) {
      for (const track of stream.getTracks()) track.stop();
      stream = null;
    }
    if (wake) {
      void wake.release().catch(() => undefined);
      wake = null;
    }
    unwatch?.();
    unwatch = null;
  }

  function finish(): void {
    const seconds_ = Math.min(seconds(), RECORD_CAP_S);
    bank();
    const type = recorder?.mimeType || chosenType || 'audio/webm';
    const blob = new Blob(chunks, { type });
    recorder = null;
    chunks = [];
    letGo();
    if (blob.size === 0) {
      setState('idle');
      events.onFail?.('empty');
      return;
    }
    setState('stopped');
    events.onDone?.({ blob, type, extension: extensionFor(type), seconds: seconds_ });
    if (capped) events.onCapped?.();
  }

  async function start(): Promise<void> {
    if (state === 'asking' || state === 'recording' || state === 'paused' || state === 'stopping') {
      return;
    }
    const Ctor = env.MediaRecorder;
    if (!env.getUserMedia || !Ctor) {
      events.onFail?.('cannot-record');
      return;
    }
    const mine = ++generation;
    banked = 0;
    segmentStart = null;
    capped = false;
    chunks = [];
    setState('asking');
    let got: MediaStream;
    try {
      got = await env.getUserMedia({ audio: true });
    } catch (err) {
      if (mine !== generation) return;
      setState('idle');
      events.onFail?.(failureOf(err));
      return;
    }
    if (mine !== generation) {
      for (const track of got.getTracks()) track.stop();
      return;
    }
    stream = got;
    chosenType = pickType(Ctor.isTypeSupported?.bind(Ctor));
    let rec: RecorderLike;
    try {
      rec = new Ctor(got, {
        ...(chosenType ? { mimeType: chosenType } : {}),
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
      });
    } catch {
      letGo();
      setState('idle');
      events.onFail?.('cannot-record');
      return;
    }
    recorder = rec;
    rec.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunks.push(event.data);
    };
    rec.onstop = () => {
      if (recorder === rec) finish();
    };
    rec.onerror = () => {
      if (recorder !== rec) return;
      recorder = null;
      chunks = [];
      bank();
      letGo();
      setState('idle');
      events.onFail?.('failed');
    };
    // The microphone going away (unplugged, revoked) ends the recording with what it has.
    for (const track of got.getAudioTracks()) {
      track.addEventListener('ended', () => {
        if (recorder === rec && (state === 'recording' || state === 'paused')) stop();
      });
    }
    try {
      // A slice a second, so a long recording is held as many small pieces.
      rec.start(1000);
    } catch {
      recorder = null;
      letGo();
      setState('idle');
      events.onFail?.('cannot-record');
      return;
    }
    segmentStart = env.now();
    level = env.levelReader?.(got) ?? null;
    ticker = env.setInterval(tick, TICK_MS);
    setState('recording');
    events.onTick?.(0);
    void holdScreen();
    watchVisibility();
  }

  function pause(): void {
    if (state !== 'recording' || !recorder?.pause) return;
    try {
      recorder.pause();
    } catch {
      return;
    }
    bank();
    events.onLevel?.(0);
    setState('paused');
  }

  function resume(): void {
    if (state !== 'paused' || !recorder?.resume) return;
    try {
      recorder.resume();
    } catch {
      return;
    }
    segmentStart = env.now();
    setState('recording');
  }

  function stop(): void {
    if ((state !== 'recording' && state !== 'paused') || !recorder) return;
    // The timer stops here, at the moment the visitor pressed Stop, not when the
    // recorder hands over its last data.
    const total = Math.min(seconds(), RECORD_CAP_S);
    banked = total;
    segmentStart = null;
    if (ticker !== null) {
      env.clearInterval(ticker);
      ticker = null;
    }
    setState('stopping');
    try {
      recorder.stop();
    } catch {
      finish();
    }
  }

  function discard(): void {
    generation += 1;
    const rec = recorder;
    recorder = null;
    chunks = [];
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch {
        /* already stopped */
      }
    }
    letGo();
    banked = 0;
    segmentStart = null;
    setState('idle');
  }

  return {
    get state() {
      return state;
    },
    get canPause() {
      const proto = env.MediaRecorder?.prototype as Partial<RecorderLike> | undefined;
      return typeof proto?.pause === 'function' && typeof proto?.resume === 'function';
    },
    get type() {
      return recorder?.mimeType || chosenType;
    },
    seconds: () => Math.min(seconds(), RECORD_CAP_S),
    start,
    pause,
    resume,
    stop,
    discard,
  };
}
